import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { BookOpen, ArrowLeft, ArrowRight, GraduationCap, Lock, AlertCircle } from "lucide-react";
import NavBar from "@/components/NavBar";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export default function CategorySubjectsPage() {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userCategory, setUserCategory] = useState(null);
  const [checkingLock, setCheckingLock] = useState(true);

  useEffect(() => {
    checkUserCategory();
    fetchSubjects();
  }, [categoryId]);

  const checkUserCategory = async () => {
    try {
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      const selectedCategory = storedUser.selected_category;
      
      // If user has selected category and it's not PRT and it's different from current
      if (selectedCategory && selectedCategory !== 'prt' && selectedCategory !== categoryId) {
        setUserCategory(selectedCategory);
      }
    } catch (error) {
      console.error("Failed to check user category:", error);
    } finally {
      setCheckingLock(false);
    }
  };

  const fetchSubjects = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/subjects?exam_type=${categoryId}`);
      const data = await response.json();
      setSubjects(data.subjects || []);
    } catch (error) {
      console.error("Failed to fetch subjects:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubjectClick = async (subjectValue) => {
    try {
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      const sessionToken = localStorage.getItem('session_token');
      
      // If category is not PRT and user hasn't selected a category yet, save it
      if (categoryId !== 'prt' && !storedUser.selected_category && storedUser.id && sessionToken) {
        const formData = new FormData();
        formData.append('user_id', storedUser.id);
        formData.append('session_token', sessionToken);
        formData.append('selected_category', categoryId);
        
        await fetch(`${BACKEND_URL}/api/auth/update-selected-category`, {
          method: 'POST',
          body: formData
        });
        
        // Update local storage
        storedUser.selected_category = categoryId;
        localStorage.setItem('user', JSON.stringify(storedUser));
      }
    } catch (error) {
      console.error("Failed to save category:", error);
    }
    
    navigate(`/${categoryId}/${subjectValue}`);
  };

  if (loading || checkingLock) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-2xl text-gray-600">Loading subjects...</div>
      </div>
    );
  }

  // If user has locked category and trying to access different one
  if (userCategory && userCategory !== categoryId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <NavBar />
        <div className="max-w-2xl mx-auto px-4 py-12">
          <Alert className="border-amber-300 bg-amber-50">
            <Lock className="h-5 w-5 text-amber-600" />
            <AlertDescription className="text-amber-800">
              <p className="font-semibold mb-2">Category Locked</p>
              <p className="mb-3">You have already selected <span className="font-bold uppercase">{userCategory}</span> as your exam category. This selection is locked to ensure focused preparation.</p>
              <p className="text-sm">To change your category, please use the "Change Subject" option in your profile menu.</p>
            </AlertDescription>
          </Alert>
          <div className="mt-6 flex gap-3">
            <Button onClick={() => navigate(`/category/${userCategory}`)} className="flex-1">
              Go to My Category ({userCategory.toUpperCase()})
            </Button>
            <Button variant="outline" onClick={() => navigate('/category')}>
              Back
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const categoryLabel = {
    tgt: "TGT (Trained Graduate Teacher)",
    pgt: "PGT (Post Graduate Teacher)",
    prt: "PRT (Primary Teacher)",
    non_teaching: "Non-Teaching Staff"
  }[categoryId] || categoryId?.toUpperCase();
  
  const categoryUpper = categoryId?.toUpperCase() || "";
  const subjectNames = subjects.map(s => s.label).join(", ");
  
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://descriptivetest.com/" },
      { "@type": "ListItem", "position": 2, "name": "Exam Categories", "item": "https://descriptivetest.com/category" },
      { "@type": "ListItem", "position": 3, "name": categoryLabel, "item": `https://descriptivetest.com/category/${categoryId}` }
    ]
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <Helmet>
        <title>EMRS KVS NVS {categoryUpper} Subjects 2026 | {categoryLabel} Tier-2 Preparation</title>
        <meta name="description" content={`Complete list of ${categoryUpper} subjects for EMRS, KVS, NVS Tier-2 exam preparation. ${subjectNames || "Hindi, English, Mathematics, Science"}. Practice mock tests, descriptive questions, MCQs, and study NCERT notes for ${categoryLabel}.`} />
        <meta name="keywords" content={`EMRS ${categoryUpper} subjects, KVS ${categoryUpper} subjects, NVS ${categoryUpper}, ${categoryUpper} exam subjects 2026, EMRS ${categoryUpper} syllabus, KVS ${categoryUpper} preparation, ${categoryUpper} tier 2 exam, ${subjects.map(s => `${categoryUpper} ${s.label}`).join(", ")}`} />
        <meta property="og:title" content={`${categoryUpper} Subjects - EMRS KVS NVS Preparation`} />
        <meta property="og:description" content={`${categoryUpper} subjects for Tier-2 exam preparation: ${subjectNames}`} />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={`https://descriptivetest.com/category/${categoryId}`} />
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>
      {/* NavBar with profile */}
      <NavBar />

      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <button
            onClick={() => navigate("/category")}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Categories</span>
          </button>
          <h1 className="text-4xl font-bold text-gray-900">
            {categoryId.toUpperCase()} Subjects
          </h1>
          <p className="text-lg text-gray-600 mt-2">
            Select a subject to view syllabus and start preparation
          </p>
          {categoryId === 'prt' && (
            <Alert className="mt-4 border-green-300 bg-green-50">
              <AlertCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800 text-sm">
                <span className="font-semibold">PRT Advantage:</span> You can browse all subjects freely. Your selection is not locked.
              </AlertDescription>
            </Alert>
          )}
        </div>
      </div>

      {/* Subjects Grid */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
          {subjects.map((subject) => (
            <div
              key={subject.value}
              onClick={() => handleSubjectClick(subject.value)}
              className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer border-2 border-transparent hover:border-blue-500 p-6 group"
            >
              <div className="flex items-center justify-center mb-4">
                <div className="p-4 bg-blue-50 rounded-full group-hover:bg-blue-100 transition-colors">
                  <BookOpen className="w-8 h-8 text-blue-600" />
                </div>
              </div>
              <h3 className="text-lg font-semibold text-center text-gray-900 mb-2">
                {subject.label}
              </h3>
              <div className="flex items-center justify-center gap-2 text-blue-600 group-hover:text-blue-700 text-sm font-medium">
                <span>View Syllabus</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
